import * as z from 'zod';

export const AggregateConfigSchema = z.object({
  // unique id minted at creation (same createRandomId() scheme sections use)
  id: z.string(),
  function: z.enum(['sum', 'avg', 'min', 'max', 'count', 'median']),
  // number formatting is inherited from the column's own config (see ColumnConfigModal / TableFooter)
});

export type AggregateConfig = z.infer<typeof AggregateConfigSchema>;

export const ColumnConfigSchema = z.object({
  custom_title: z.string().optional(),
  date_format: z.string().optional(),
  time_format: z.string().optional(),
  disable_time: z.boolean().optional(),
  mirror_column_formatter: z.string().optional(),
  mirror_column_done_status: z.string().optional(),
  grid_label_visibility: z.string().optional(), // 'auto', 'show', 'hide'
  enable_file_viewer: z.boolean().optional(),
  enable_compact_mode: z.boolean().optional(),
  enable_custom_number_format: z.boolean().optional(),
  disable_tag_splitting: z.boolean().optional(),
  number_format_locale: z.string().optional(),
  number_format_style: z.string().optional(),
  number_format_currency: z.string().optional(),
  number_format_unit: z.string().optional(),
  number_format_min_fraction_digits: z.number().optional(),
  number_format_max_fraction_digits: z.number().optional(),
});

type ColumnConfigValue = {
  // any other key is a column id
  [columnId: string]: {
    type?: string;
    visible?: boolean;
    editable?: boolean;
    scope?: 'overview' | 'detail' | 'overview-detail';
    config?: z.infer<typeof ColumnConfigSchema>;
    aggregate?: z.infer<typeof AggregateConfigSchema>;
  };
} & {
  ___meta___?: {
    sortingMode?: 'board' | 'manual';
    columnsOrdered?: string[];
  };
};

const ColumnConfigValueSchema = z.record(
  z.string(),
  z.object({
    type: z.string().optional(),
    visible: z.boolean().optional(),
    editable: z.boolean().optional(),
    scope: z.enum(['overview', 'detail', 'overview-detail']).optional(),
    config: ColumnConfigSchema.optional(),
    aggregate: AggregateConfigSchema.optional(),
  }),
);

const ColumnsConfigMetaSchema = z.object({
  sortingMode: z.enum(['board', 'manual']).optional(),
  columnsOrdered: z.array(z.string()).optional(),
});

export const ColumnSettingsSchema = z
  .custom<ColumnConfigValue>((value) => {
    // fail validation if it's not a plain object
    if (typeof value !== 'object' || Array.isArray(value) || value === null) {
      return false;
    }

    return true;
  })
  .refine((value) => {
    if (!('___meta___' in value)) {
      return true;
    }

    return ColumnsConfigMetaSchema.safeParse(value['___meta___']).success;
  }, 'Invalid meta data')
  .refine((value) => {
    const { ___meta___, ...columnsConfig } = value;

    return ColumnConfigValueSchema.safeParse(columnsConfig).success;
  }, 'Invalid column config');

const SharedClientBoardSourceSchema = z.object({
  type: z.literal('shared-client-board'),
  id: z.string(),
  boardId: z.number(),
  clientColumnId: z.string(),
  name: z.string(),
});

const ClientBoardSourceSchema = z.object({
  type: z.literal('client-board'),
  id: z.string(),
  templateBoardId: z.number(),
  name: z.string(),
});

const BoardBoardSourceSchema = z.object({
  type: z.literal('board'),
  id: z.string(),
  boardId: z.number(),
  name: z.string(),
});

export const BOARD_SOURCE_TYPES = ['shared-client-board', 'client-board', 'board'] as const;

export const BoardSourceSchema = z.union([SharedClientBoardSourceSchema, ClientBoardSourceSchema, BoardBoardSourceSchema]);

//export const BoardSourceFieldValueSchema = BoardSourceSchema;

export const BoardSourceFieldValueSchema = z.object({
  type: z.string().optional(),
  boardSourceId: z.string().optional(),
});

export const ButtonItemActionSchema = z.object({
  id: z.string(),
  type: z.literal('button'),
  label: z.string(),
  actionId: z.string(),
  kind: z.enum(['primary', 'secondary']).optional(),
  conditions: z.any().optional(),
  conditionsOperator: z.enum(['and', 'or']).optional(),
});

export type ButtonItemAction = z.infer<typeof ButtonItemActionSchema>;

export const ItemActionsFieldValueSchema = z.object({
  actions: z.array(ButtonItemActionSchema),
});

export const TextFieldValueSchema = z.union([z.string(), z.number()]);
export const HeadlineFieldValueSchema = z.object({ headline: z.string(), level: z.number() });
export const TeasersFieldValueSchema = z.array(
  z.object({ headline: z.string(), text: z.string(), url: z.string(), backgroundColor: z.string().optional() }),
);
export const KEY_FIGURE_FUNCTIONS = ['sum', 'avg', 'min', 'max', 'median', 'count', 'count_items'] as const;

// A key figure is a number computed over an item-table or item-grid section (on any page), with that section's
// board source, filters and client scoping. count_items counts the items and takes no column; every other function
// needs a numbers column. The id is minted when the figure is added and ties the computed value to its card.
export const KeyFigureSchema = z.object({
  id: z.string().optional(),
  icon: z.string().optional(),
  label: z.string().optional(),
  url: z.string().optional(),
  backgroundColor: z.string().optional(),
  sectionId: z.string().optional(),
  columnId: z.string().optional(),
  function: z.enum(KEY_FIGURE_FUNCTIONS).optional(),
});

export const KeyFiguresFieldValueSchema = z.array(KeyFigureSchema);
export const BoardColumnFieldValueSchema = z.object({ columnId: z.string() });
export const TextareaFieldValueSchema = z.string();
export const ItemCommentsFieldValueSchema = z.object({
  enabled: z.boolean(),
  filterMode: z.enum(['only-client', 'all-clients', 'all']).optional(),
});
export const ItemsPerPageFieldValueSchema = z.number();
export const ColumnSettingsFieldValueSchema = ColumnSettingsSchema;
export const BoardFiltersFieldValueSchema = z.any(); // TODO: make more specific
export const SelectFieldValueSchema = z.object({ value: z.string() });
export const ItemSearchFieldValueSchema = z.object({
  enabled: z.boolean(),
});
export type ItemSearchFieldValue = z.infer<typeof ItemSearchFieldValueSchema>;

export const DisableAutoFetchFieldValueSchema = z.object({
  enabled: z.boolean().optional(),
});
export type DisableAutoFetchFieldValue = z.infer<typeof DisableAutoFetchFieldValueSchema>;

export const HideReloadButtonFieldValueSchema = z.object({
  enabled: z.boolean().optional(),
});
export type HideReloadButtonFieldValue = z.infer<typeof HideReloadButtonFieldValueSchema>;

// const FieldValuesSchema = z.record(FieldValueSchema);
const FieldValuesSchema = z.record(z.string(), z.any());

export type FieldValues = z.infer<typeof FieldValuesSchema>;

const SectionSchema = z.object({
  id: z.string(),
  type: z.string(),
  fieldValues: FieldValuesSchema,
  conditions: z.any().optional(), // TODO: make more specific
  conditionsOperator: z.enum(['and', 'or']).optional(),
  hidden: z.boolean().optional(),
});

export const PageSchema = z.object({
  id: z.string(),
  name: z.string(),
  //position: z.string(),
  sections: z.array(SectionSchema),
  menu: z.enum(['main', 'footer']).optional(),
  icon: z.string().optional(),
  conditions: z.any().optional(), // TODO: make more specific
  conditionsOperator: z.enum(['and', 'or']).optional(),
  hidden: z.boolean().optional(),
});

export const ThemeSchema = z.object({
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  teritaryColor: z.string().optional(),
  specialColor: z.string().optional(),
  successColor: z.string().optional(),
  warningColor: z.string().optional(),
  errorColor: z.string().optional(),
  logo: z.string().optional(),
  fontFamily: z.string().optional(),
  sidebarCollapsedByDefault: z.boolean().optional(),
});

export const AuthSchema = z.object({
  portalId: z.string(),
  email: z.email(),
  password: z.string(),
});

export const GoogleAuthSchema = z.object({
  portalId: z.string(),
  idToken: z.string(),
});

export const LoginPageSchema = z.object({
  introText: z.string().optional(),
});

export const PortalSettingsSchema = z.object({
  version: z.enum(['2']),
  name: z.string(),
  theme: ThemeSchema,
  extensionKey: z.string().optional(),
  boardSources: z.array(BoardSourceSchema),
  pages: z.array(PageSchema),
  loginPage: LoginPageSchema.optional(),

  placeholderColumnTypes: z.array(z.string()).optional(),

  enableGoogleAuth: z.boolean().optional(),
  googleClientId: z.string().optional(),
  googleWorkspaceDomain: z.string().optional(),

  googleAnalyticsId: z.string().optional(),

  allowUpdatePassword: z.boolean().optional(),

  lang: z.enum(['en', 'de', 'fr', 'es', 'pt', 'pl', 'he', 'zh']).optional(),

  redirectSubdomainToCustomDomain: z.boolean().optional(),

  enableClientInbox: z.boolean().optional(),
  enableCache: z.boolean().optional(),
  enableMismatchHandling: z.boolean().optional(),
});

export const FileUploadSchema = z.object({
  name: z.string(),
  content: z.string(),
});

export const CreatePreviewTokenSchema = z.object({
  clientItemId: z.number(),
  scope: z.enum(['public', 'draft']),
});

export const PortalsLookupSchema = z.object({
  portalIds: z.array(z.string()),
});

export const CreateActivitySchema = z.object({
  portalId: z.string(),
  clientItemId: z.number(),
  itemId: z.number().optional(),
  message: z.string(),
});

export const TriggerCommentAutomationSchema = z.object({
  portalId: z.string(),
  clientItemId: z.number(),
  itemId: z.number(),
  comment: z.string(),
});

export const SlotSettingsSchema = z.object({
  boardId: z.number(),
});

export const CredentialsAccountSchemaBase = z.object({
  type: z.literal('credentials'),
  email: z.email(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  //passwordHash: z.string(),
  note: z.string().optional(),
  enabled: z.boolean(),
});

export const GoogleAccountSchemaBase = z.object({
  type: z.literal('google'),
  email: z.email(),
  note: z.string().optional(),
  enabled: z.boolean(),
});

function checkPassword(password: string, checkPassComplexity: z.RefinementCtx) {
  const containsUppercase = (ch: string) => /[A-Z]/.test(ch);
  const containsLowercase = (ch: string) => /[a-z]/.test(ch);
  const containsSpecialChar = (ch: string) => /[`!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?~ ]/.test(ch);

  let countOfUpperCase = 0;
  let countOfLowerCase = 0;
  let countOfNumbers = 0;
  let countOfSpecialChar = 0;

  for (let i = 0; i < password.length; i++) {
    let ch = password.charAt(i);
    if (!isNaN(+ch)) {
      countOfNumbers++;
    } else if (containsUppercase(ch)) {
      countOfUpperCase++;
    } else if (containsLowercase(ch)) {
      countOfLowerCase++;
    } else if (containsSpecialChar(ch)) {
      countOfSpecialChar++;
    }
  }

  if (password.length < 8 || countOfSpecialChar < 1 || countOfNumbers < 1) {
    checkPassComplexity.addIssue({
      code: 'custom',
      message: 'Password must have at least 8 characters, including one number and one symbol.',
    });
  }
}

export const CredentialsAccountUpdateSchema = z
  .object({
    updatePassword: z.boolean(),
    password: z.string().optional(),
    firstname: z.string().optional(),
    lastname: z.string().optional(),
    note: z.string().optional(),
    enabled: z.boolean(),
  })
  .superRefine(({ updatePassword, password }, checkPassComplexity) => {
    if (updatePassword) {
      checkPassword(password || '', checkPassComplexity);
    }
  });

export const UpdatePasswordSchema = z
  .object({
    oldPassword: z.string(),
    newPassword: z.string(),
  })
  .superRefine(({ newPassword }, checkPassComplexity) => {
    checkPassword(newPassword || '', checkPassComplexity);
  });

export const GoogleAccountUpdateSchema = z.object({
  note: z.string().optional(),
  enabled: z.boolean(),
});

export const CredentialsAccountSchemaWithPassword = CredentialsAccountSchemaBase.extend({
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  password: z.string(),
}).superRefine(({ password }, checkPassComplexity) => {
  checkPassword(password, checkPassComplexity);
});

export const CredentialsAccountSchemaWithPasswordHash = CredentialsAccountSchemaBase.extend({
  passwordHash: z.string(),
});

export const GoogleAccountSchema = z.object({
  type: z.literal('google'),
  email: z.string(),
  note: z.string().optional(),
  enabled: z.boolean(),
});

export const AccountCreateSchema = z.union([CredentialsAccountSchemaWithPassword, GoogleAccountSchemaBase]);
export const AccountImportSchema = z.union([CredentialsAccountSchemaWithPasswordHash, GoogleAccountSchemaBase]);
export const AccountSchema = z.union([CredentialsAccountSchemaBase, GoogleAccountSchemaBase]);

export const ClientBoardItemSchemaBase = z.object({
  slotSettings: z.record(z.string(), SlotSettingsSchema),
  enabled: z.boolean(),
});

export const AccountsSchema = z.array(AccountSchema);

export const ClientSettingsSchema = ClientBoardItemSchemaBase.extend({
  accounts: AccountsSchema,
});

export const SharedClientBoardLoadFirstPageActionSchema = z.object({
  action: z.literal('load-first-page'),
  searchTerm: z.string().max(200).optional(),
  searchColumnId: z.string().max(100).optional(),
  sortColumnId: z.string().max(100).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});

export const SharedClientBoardLoadPageByCursorActionSchema = z.object({
  action: z.literal('load-page-by-cursor'),
  cursor: z.string(),
  searchTerm: z.string().max(200).optional(),
  searchColumnId: z.string().max(100).optional(),
  sortColumnId: z.string().max(100).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});

export const SharedClientBoardActionSchema = z.union([
  SharedClientBoardLoadFirstPageActionSchema,
  SharedClientBoardLoadPageByCursorActionSchema,
]);

export const BoardLoadFirstPageActionSchema = z.object({
  action: z.literal('load-first-page'),
  searchTerm: z.string().max(200).optional(),
  searchColumnId: z.string().max(100).optional(),
  sortColumnId: z.string().max(100).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});

export const BoardLoadPageByCursorActionSchema = z.object({
  action: z.literal('load-page-by-cursor'),
  cursor: z.string(),
  searchTerm: z.string().max(200).optional(),
  searchColumnId: z.string().max(100).optional(),
  sortColumnId: z.string().max(100).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});

// Answers a key figures section: only its figures that are computed over this section, without items.
// Which columns and functions are aggregated comes from the stored key figures config, never from the request.
export const BoardLoadAggregatesActionSchema = z.object({
  action: z.literal('load-aggregates'),
  keyFiguresSectionId: z.string().max(100),
});

export const BoardActionSchema = z.union([
  SharedClientBoardLoadFirstPageActionSchema,
  SharedClientBoardLoadPageByCursorActionSchema,
  BoardLoadAggregatesActionSchema,
]);

export const ItemActionSchema = z.object({
  actionId: z.string(),
});

export const ItemUpdateSchema = z.object({
  columnId: z.string(),
  value: z.any(),
});

export const AddCommentSchema = z.object({
  parentId: z.string().optional(),
  message: z.string(),
});

export const FileStreamUploadSchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
});

export type ClientBoardSource = z.infer<typeof ClientBoardSourceSchema>;
export type SharedClientBoardSource = z.infer<typeof SharedClientBoardSourceSchema>;
export type BoardBoardSource = z.infer<typeof BoardBoardSourceSchema>;

export type SharedClientBoardAction = z.infer<typeof SharedClientBoardActionSchema>;
export type BoardSource = z.infer<typeof BoardSourceSchema>;
export type Section = z.infer<typeof SectionSchema>;
export type SectionTypes = Section['type'];
export type Page = z.infer<typeof PageSchema>;
export type TextFieldValue = z.infer<typeof TextFieldValueSchema>;
export type TeasersFieldValue = z.infer<typeof TeasersFieldValueSchema>;
export type KeyFiguresFieldValue = z.infer<typeof KeyFiguresFieldValueSchema>;
export type KeyFigureFunction = (typeof KEY_FIGURE_FUNCTIONS)[number];
export type TextareaFieldValue = z.infer<typeof TextareaFieldValueSchema>;
export type ItemsPerPageFieldValue = z.infer<typeof ItemsPerPageFieldValueSchema>;
export type ItemCommentsFieldValue = z.infer<typeof ItemCommentsFieldValueSchema>;
export type HeadlineFieldValue = z.infer<typeof HeadlineFieldValueSchema>;
export type BoardSourceFieldValue = z.infer<typeof BoardSourceFieldValueSchema>;
export type ColumnSettingsFieldValue = z.infer<typeof ColumnSettingsFieldValueSchema>;
export type BoardFiltersFieldValue = z.infer<typeof BoardFiltersFieldValueSchema>;
export type BoardColumnFieldValue = z.infer<typeof BoardColumnFieldValueSchema>;
// export type TextSection = z.infer<typeof TextSectionSchema>;
// export type IframeSection = z.infer<typeof IframeSectionSchema>;
export type PortalSettings = z.infer<typeof PortalSettingsSchema>;
export type ClientSettings = z.infer<typeof ClientSettingsSchema>;
// export type ClientBoardSection = z.infer<typeof ClientBoardSectionSchema>;
// export type SharedClientBoardSection = z.infer<typeof SharedClientBoardSectionSchema>;
export type Theme = z.infer<typeof ThemeSchema>;
export type ColumnSettings = z.infer<typeof ColumnSettingsSchema>;
export type ClientSettingsUpdate = z.infer<typeof ClientBoardItemSchemaBase>;
export type Account = z.infer<typeof AccountSchema>;
export type Accounts = z.infer<typeof AccountsSchema>;
export type AccountCreate = z.infer<typeof AccountCreateSchema>;
export type AccountImport = z.infer<typeof AccountImportSchema>;
export type TriggerCommentAutomation = z.infer<typeof TriggerCommentAutomationSchema>;
export type CredentialsAccountUpdate = z.infer<typeof CredentialsAccountUpdateSchema>;
export type GoogleAccountUpdate = z.infer<typeof GoogleAccountUpdateSchema>;
export type CredentialsAccount = z.infer<typeof CredentialsAccountSchemaBase>;
export type GoogleAccount = z.infer<typeof GoogleAccountSchemaBase>;
export type BoardAction = z.infer<typeof BoardActionSchema>;
export type SelectFieldValue = z.infer<typeof SelectFieldValueSchema>;
export type ItemActionsFieldValue = z.infer<typeof ItemActionsFieldValueSchema>;
export type ColumnConfig = z.infer<typeof ColumnConfigSchema>;

export type AccountUpdate = CredentialsAccountUpdate | GoogleAccountUpdate;

export type Portal = {
  extensionId: string | null;
  settings: Omit<PortalSettings, 'boardId' | 'extensionKey'>; // TODO: no need to omit boardId, extensionKey?
  account: Pick<CredentialsAccount, 'type' | 'email'> | Pick<GoogleAccount, 'type' | 'email'>;
  portalManagerPermissions: string[];
  clientItemId: number;
  clientItemName: string;
  clientItemToken: string | null;
  region?: string;
  scope?: 'draft' | 'public';
};

export type PortalSettingsStorageFormat = PortalSettings & { boardId: number; lastUpdatedAt: number };
export type CredentialsAccountStorageFormat = CredentialsAccount & { passwordHash: string; id: string };
export type GoogleAccountStorageFormat = GoogleAccount & { id: string };
export type AccountStorageFormat = CredentialsAccountStorageFormat | GoogleAccountStorageFormat;
export type ClientSettingsStorageFormat = Omit<ClientSettings, 'accounts'> & { accounts: AccountStorageFormat[] };

export const DEFAULT_PORTAL_SETTINGS: PortalSettings = {
  version: '2',
  name: 'Client Portal',
  theme: {
    primaryColor: '#465464',
    secondaryColor: '#fcfefe',
    teritaryColor: '#465464',
    specialColor: '#e3eaf1',
    successColor: '#00c875',
    warningColor: '#fdab3d',
    errorColor: '#df2f4a',
  },
  enableCache: true,
  enableClientInbox: true,
  boardSources: [],
  pages: [
    {
      id: 'home',
      name: 'Home',
      icon: 'house',
      //position: 'sidebar',
      sections: [
        {
          id: 'text-1',
          type: 'text',
          fieldValues: {
            headline: {
              headline: 'Welcome to your Client Portal 🥳',
              level: 1,
            },
            text: `Your Client Portal is ready. This is where your clients can access shared boards, updates, and information in a clean, branded experience.

This is a preview of how the portal looks for client **{{client.name}}**. To preview the portal for **other clients**, open the client's item in your [Clients Board](https://getgorilla.app/products/client-portal/docs/getting-started/clients-board!external) and click **Preview** from the [Client Settings](https://getgorilla.app/products/client-portal/docs/getting-started/app-views/client-settings-view!external) item view.

### Getting started

- Structure pages and add content using the 👷‍♂️ **[Builder](https://getgorilla.app/products/client-portal/docs/features/builder!external)**
- Customize the look and feel in the 👩‍🎨 **[Theme](https://getgorilla.app/products/client-portal/docs/features/theme!external)** settings
- Fine-tune advanced behavior in the 👨‍🔧 **[Settings](https://getgorilla.app/products/client-portal/docs/features/settings!external)**

This page is an example of what your clients will see. You can edit, remove, or replace this content at any time.

**Happy building!**`,
          },
        },
      ],
    },
  ],
};

export const DEFAULT_CLIENT_SETTINGS: ClientSettingsStorageFormat = {
  slotSettings: {},
  enabled: false,
  accounts: [],
};
